<script setup>
import { ref, computed, nextTick, useTemplateRef } from 'vue';
import { useValidation, useFormContext } from '/app/composables/form';
import NIcon from '/app/components/NIcon.vue';

defineOptions({ inheritAttrs: false });

const props = defineProps({
    name: { type: String, default: null },
    label: { type: String, default: null },
    hint: { type: String, default: null },
    fieldName: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    forceError: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    modelValue: {
        type: Array,
        default: () => []
    },
    outlined: {
        type: Boolean,
        default: true
    },
    backgroundColor: {
        type: String,
        default: 'bg-white'
    },
    max: {
        type: Number,
        default: -1,
    },
});

const emit = defineEmits(['update:modelValue', 'added', 'change']);

const form = useFormContext();
const { invalid, error, attrs, clearErrors, setError, focus, target } = useValidation(props);
const inputRef = useTemplateRef('input');

const tag = ref('');

const tags = computed({
    get() {
        return props.modelValue;
    },
    set(v) {
        emit('update:modelValue', v);
        emit('change', v);
    }
});

function add() {
    if (props.max > 0 && tags.value.length >= props.max) {
        return;
    }

    let t = tag.value.replace(',', '').replace(';', '').trim();

    if (t && !tags.value.includes(t)) {
        tags.value = [...tags.value, t.trim()];
        tag.value = '';

        nextTick(() => {
            emit('added', t);
        });
    }
}

function remove(target) {
    tags.value = tags.value.filter(t => t !== target);
    inputRef.value.focus();
}

function removeLast() {
    if (!tag.value) {
        tags.value = tags.value.slice(0, -1);
    }
}

function reset() {
    tag.value = '';
    tags.value = props.modelValue;
}

function resolveTag(e) {
    if (!tag.value) {
        return;
    }

    switch (e.key) {
        case 'Enter':
        case 'Tab':
        case ',':
        case ';':
            e.preventDefault();
            add();
            break;
    }
}
</script>

<template>
    <label :for="name" class="form-group__helper">
        <span v-if="label" class="form-group__label" style="display: block;">{{ label }}</span>
        <span v-if="hint" class="form-group__hint" style="display: block;">{{ hint }}</span>
        <div class="form-group__tags relative border border-dust-300 rounded-md" :class="{ 'bg-dust-100': disabled, 'border': outlined, [backgroundColor]: true }">
            <template v-if="tags.length > 0" class="inline-flex items-center">
                <span v-for="(tag, index) in tags" :key="index" class="form-group__tag bg-white my-0.2 mx-1">
                    <span class="ml-1">{{ tag }}</span>
                    <NIcon as="times-circle-regular" v-if="!disabled" @click.prevent="remove(tag)" />
                </span>
            </template>
            <input
                v-bind="attrs"
                v-model.trim="tag"
                :name="name"
                :id="name"
                ref="input"
                type="text"
                class="min-h-[2.025rem] ml-1 flex-grow outline-none"
                :class="{ 'is-error': invalid, 'bg-dust-100': disabled, [backgroundColor]: true }"
                :disabled="form.busy || disabled"
                @keydown.backspace="removeLast"
                @keydown="resolveTag"
                @blur="add"
            >

            <small v-if="max > 0" class="text-dust-400 absolute bottom-1 right-2">{{ tags.length }}/{{ max }}</small>
        </div>
        <span v-if="invalid" class="form-group__error">{{ error }}</span>
    </label>
</template>
